import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getReportModuleCapabilities,
  REPORT_ACCESS_PERMISSIONS,
} from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import { runReportPreview } from '../../services/reportService';
import type { WfhReportFilters, WfhReportRow } from '../../types/wfhReport';
import {
  createDefaultWfhReportFilters,
  filterWfhReportRows,
  formatWfhReportDateRangeLabel,
  mapWfhReportRows,
  summarizeWfhReportRows,
  validateWfhReportDateRange,
  wfhSummaryApiFilters,
} from '../../utils/wfhReport';
import { reportErrorMessage } from '../../utils/reportErrors';

type SummaryMetric = {
  key: 'totalWfhDays' | 'employees' | 'departments';
  label: string;
  value: number;
  color: string;
  highlighted?: boolean;
};

type LocationOption = { id: number; label: string };

type Props = NativeStackScreenProps<RootStackParamList, 'WfhReport'>;

const REPORT_CODE = 'WFH_SUMMARY';
const REPORT_NAME = 'WFH Report';
const PREVIEW_MAX_ROWS = 500;
const EXCEL_GREEN = '#16A34A';

const COL_EMPLOYEE = 180;
const COL_DATE = 120;
const COL_TIME = 100;

function BellIcon({ color }: { color: string }) {
  return (
    <View style={styles.bellRoot} accessibilityElementsHidden>
      <View style={[styles.bellBody, { borderColor: color }]} />
      <View style={[styles.bellClapper, { backgroundColor: color }]} />
    </View>
  );
}

function CalendarGlyph({ color }: { color: string }) {
  return (
    <Text style={[styles.calendarGlyph, { color }]} accessibilityElementsHidden>
      ▦
    </Text>
  );
}

function SearchIcon({ color }: { color: string }) {
  return (
    <View style={styles.searchIcon} accessibilityElementsHidden>
      <View style={[styles.searchCircle, { borderColor: color }]} />
      <View style={[styles.searchHandle, { backgroundColor: color }]} />
    </View>
  );
}

function ExcelGlyph({ color }: { color: string }) {
  return (
    <View
      style={[styles.excelGlyph, { borderColor: color }]}
      accessibilityElementsHidden
    >
      <Text style={[styles.excelGlyphText, { color }]}>X</Text>
    </View>
  );
}

function WfhTableRow({
  row,
  palette,
  isLast,
}: {
  row: WfhReportRow;
  palette: ColorPalette;
  isLast: boolean;
}) {
  return (
    <View
      style={[
        styles.bodyRow,
        {
          borderBottomColor: palette.border,
          borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
        },
      ]}
      accessibilityLabel={`${row.employee}, ${row.dateLabel}, check in ${row.checkIn}, check out ${row.checkOut}`}
    >
      <View style={[styles.employeeCell, { width: COL_EMPLOYEE }]}>
        <ProfileAvatar name={row.employee === '—' ? '?' : row.employee} size={36} />
        <Text
          style={[styles.employeeName, { color: palette.textPrimary }]}
          numberOfLines={2}
        >
          {row.employee}
        </Text>
      </View>
      <Text
        style={[styles.cellText, { width: COL_DATE, color: palette.textPrimary }]}
        numberOfLines={1}
      >
        {row.dateLabel}
      </Text>
      <Text
        style={[styles.cellText, { width: COL_TIME, color: palette.textPrimary }]}
        numberOfLines={1}
      >
        {row.checkIn}
      </Text>
      <Text
        style={[styles.cellText, { width: COL_TIME, color: palette.textPrimary }]}
        numberOfLines={1}
      >
        {row.checkOut}
      </Text>
    </View>
  );
}

export function WfhReportScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getReportModuleCapabilities(auth.permissions);
  const downloads = useReportDownloads();

  const defaults = useMemo(() => createDefaultWfhReportFilters(), []);
  const [draft, setDraft] = useState<WfhReportFilters>(defaults);
  const [applied, setApplied] = useState<WfhReportFilters>(defaults);

  const [rows, setRows] = useState<WfhReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [openPicker, setOpenPicker] = useState<'department' | 'location' | null>(
    null,
  );
  const [selectedDepartmentLabel, setSelectedDepartmentLabel] = useState<
    string | null
  >(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(
    null,
  );
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [locationDepartmentNames, setLocationDepartmentNames] = useState<
    string[] | null
  >(null);
  const hasFocused = useRef(false);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  const themed = useMemo(
    () => ({
      error: { ...typography.body, color: palette.error, marginTop: spacing.sm },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.sm,
      },
      headerCell: {
        ...typography.caption,
        fontWeight: '700' as const,
        color: palette.textMuted,
      },
    }),
    [palette],
  );

  const dateRangeLabel = formatWfhReportDateRangeLabel(
    draft.fromDate,
    draft.toDate,
  );

  const fetchReport = useCallback(
    async (filters: WfhReportFilters, isRefresh = false) => {
      if (!capabilities.canPreview) {
        setError('You do not have permission to preview this report.');
        setLoading(false);
        return;
      }

      const rangeError = validateWfhReportDateRange(
        filters.fromDate,
        filters.toDate,
      );
      if (rangeError) {
        setFilterError(rangeError);
        setError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const apiFilters = wfhSummaryApiFilters(filters.fromDate, filters.toDate);
      if (!apiFilters) {
        setFilterError('Unable to resolve the selected date range.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      setFilterError(null);
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const preview = await runReportPreview({
          reportCode: REPORT_CODE,
          filters: apiFilters,
          maxRows: PREVIEW_MAX_ROWS,
        });
        setRows(mapWfhReportRows(preview.rows));
        setTruncated(preview.truncated);
        setApplied(current =>
          current.fromDate === filters.fromDate &&
          current.toDate === filters.toDate &&
          current.department === filters.department
            ? current
            : filters,
        );
        setError(null);
      } catch (err) {
        setRows([]);
        setError(reportErrorMessage(err, 'Unable to load WFH report.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canPreview],
  );

  useEffect(() => {
    let cancelled = false;
    getLocations({ page: 1, pageSize: 100, filters: { isActive: true } })
      .then(result => {
        if (cancelled) return;
        setLocationOptions(
          result.items.map(item => ({
            id: item.locationId,
            label: item.locationName,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setLocationOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      const isRefresh = hasFocused.current;
      hasFocused.current = true;
      fetchReport(appliedRef.current, isRefresh).catch(() => undefined);
    }, [fetchReport]),
  );

  const departmentOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of rows) {
      if (row.department && row.department !== '—') {
        names.add(row.department);
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const departmentRows = useMemo(() => {
    let next = filterWfhReportRows(rows, { department: applied.department });
    if (locationDepartmentNames) {
      const allowed = new Set(
        locationDepartmentNames.map(name => name.trim().toLowerCase()),
      );
      next = next.filter(
        row =>
          row.department != null &&
          allowed.has(row.department.trim().toLowerCase()),
      );
    }
    return next;
  }, [applied.department, locationDepartmentNames, rows]);

  const visibleRows = useMemo(() => {
    const query = employeeQuery.trim().toLowerCase();
    if (!query) return departmentRows;
    return departmentRows.filter(row =>
      row.employee.toLowerCase().includes(query),
    );
  }, [departmentRows, employeeQuery]);

  const summary = useMemo(
    () => summarizeWfhReportRows(departmentRows),
    [departmentRows],
  );

  const summaryMetrics = useMemo<SummaryMetric[]>(
    () => [
      {
        key: 'totalWfhDays',
        label: 'Total WFH Days',
        value: summary.totalWfhDays,
        color: palette.info,
        highlighted: true,
      },
      {
        key: 'employees',
        label: 'Employees',
        value: summary.employees,
        color: palette.textPrimary,
      },
      {
        key: 'departments',
        label: 'Departments',
        value: summary.departments,
        color: palette.info,
      },
    ],
    [palette.info, palette.textPrimary, summary],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.includes(`${REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const updateDraft = (field: 'fromDate' | 'toDate', value: string) => {
    setDraft(current => ({ ...current, [field]: value }));
    setFilterError(null);
    setDownloadError(null);
  };

  const applyDateRange = () => {
    const next = {
      ...draft,
      fromDate: draft.fromDate.trim(),
      toDate: draft.toDate.trim(),
    };
    setDraft(next);
    setShowDatePicker(false);
    fetchReport(next).catch(() => undefined);
  };

  const selectDepartment = (label: string | null) => {
    setSelectedDepartmentLabel(label);
    setOpenPicker(null);
    const nextDepartment = label ?? '';
    setDraft(current => ({ ...current, department: nextDepartment }));
    setApplied(current =>
      current.department === nextDepartment
        ? current
        : { ...current, department: nextDepartment },
    );
  };

  const selectLocation = async (option: LocationOption | null) => {
    setSelectedLocation(option);
    setOpenPicker(null);
    if (!option) {
      setLocationDepartmentNames(null);
      return;
    }
    try {
      const result = await getDepartments({
        page: 1,
        pageSize: 100,
        filters: { locationId: option.id, isActive: true },
      });
      setLocationDepartmentNames(
        result.items.map(item => item.departmentName).filter(Boolean),
      );
    } catch {
      setLocationDepartmentNames([]);
    }
  };

  const exportReport = async (format: 'CSV' | 'XLSX') => {
    setDownloadError(null);
    const next = {
      fromDate: draft.fromDate.trim(),
      toDate: draft.toDate.trim(),
      department: draft.department.trim(),
    };
    const rangeError = validateWfhReportDateRange(next.fromDate, next.toDate);
    if (rangeError) {
      setFilterError(rangeError);
      return;
    }
    const apiFilters = wfhSummaryApiFilters(next.fromDate, next.toDate);
    if (!apiFilters) {
      setFilterError('Unable to resolve the selected date range.');
      return;
    }

    const allowed =
      format === 'CSV'
        ? capabilities.canExportCsv
        : capabilities.canExportXlsx;
    if (!allowed) {
      setDownloadError('You do not have permission to export reports.');
      return;
    }
    if (downloads.isDownloadInFlight(REPORT_CODE, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }

    try {
      const result = await downloads.startDownload({
        reportCode: REPORT_CODE,
        reportName: REPORT_NAME,
        format,
        filters: apiFilters,
        view: {
          columns: [
            { key: 'employee', label: 'Employee' },
            { key: 'date', label: 'Date' },
            { key: 'checkIn', label: 'Check In' },
            { key: 'checkOut', label: 'Check Out' },
          ],
          rows: visibleRows.map(row => ({
            employee: row.employee,
            date: row.dateLabel,
            checkIn: row.checkIn,
            checkOut: row.checkOut,
          })),
        },
      });
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(reportErrorMessage(err, 'The report could not be saved.'));
    }
  };

  const departmentButtonLabel = selectedDepartmentLabel ?? 'All Departments';
  const locationButtonLabel = selectedLocation?.label ?? 'All Locations';

  const headerRight = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Unread notifications"
      onPress={() => navigation.navigate(routes.notificationList)}
      hitSlop={8}
    >
      <BellIcon color={palette.white} />
    </Pressable>
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <PermissionGuard
        anyPermissions={[...REPORT_ACCESS_PERMISSIONS]}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen
          scroll
          keyboardSafe
          webLayout="wide"
          backgroundColor={palette.background}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => fetchReport(applied, true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader
            title={REPORT_NAME}
            onBack={navigation.goBack}
            right={headerRight}
          />

          <View style={styles.body}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Date range ${dateRangeLabel}`}
              accessibilityState={{ expanded: showDatePicker }}
              onPress={() => setShowDatePicker(current => !current)}
              style={[
                styles.dateRangeField,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <Text
                style={[styles.dateRangeLabel, { color: palette.textPrimary }]}
                numberOfLines={1}
              >
                {dateRangeLabel}
              </Text>
              <CalendarGlyph color={palette.textMuted} />
            </Pressable>

            {showDatePicker ? (
              <View
                style={[
                  styles.advancedCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                    shadowColor: palette.black,
                  },
                ]}
              >
                <AppDateField
                  label="From date"
                  value={draft.fromDate}
                  onChange={value => updateDraft('fromDate', value)}
                  maximumDate={
                    draft.toDate.trim()
                      ? new Date(`${draft.toDate.trim()}T12:00:00`)
                      : undefined
                  }
                />
                <AppDateField
                  label="To date"
                  value={draft.toDate}
                  onChange={value => updateDraft('toDate', value)}
                  minimumDate={
                    draft.fromDate.trim()
                      ? new Date(`${draft.fromDate.trim()}T12:00:00`)
                      : undefined
                  }
                />
                {filterError ? (
                  <Text accessibilityRole="alert" style={themed.error}>
                    {filterError}
                  </Text>
                ) : null}
                <AppButton title="Apply" onPress={applyDateRange} />
              </View>
            ) : null}

            {filterError && !showDatePicker ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {filterError}
              </Text>
            ) : null}
            {downloadError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {downloadError}
              </Text>
            ) : null}

            <View style={styles.filterRow}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={departmentButtonLabel}
                accessibilityState={{ expanded: openPicker === 'department' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'department' ? null : 'department',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {departmentButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={locationButtonLabel}
                accessibilityState={{ expanded: openPicker === 'location' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'location' ? null : 'location',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {locationButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
            </View>

            {openPicker === 'department' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Departments"
                  onPress={() => selectDepartment(null)}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Departments
                  </Text>
                </Pressable>
                {departmentOptions.map(name => (
                  <Pressable
                    key={name}
                    accessibilityRole="button"
                    accessibilityLabel={name}
                    onPress={() => selectDepartment(name)}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>{name}</Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            {openPicker === 'location' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Locations"
                  onPress={() => {
                    selectLocation(null).catch(() => undefined);
                  }}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Locations
                  </Text>
                </Pressable>
                {locationOptions.map(option => (
                  <Pressable
                    key={option.id}
                    accessibilityRole="button"
                    accessibilityLabel={option.label}
                    onPress={() => {
                      selectLocation(option).catch(() => undefined);
                    }}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            <View
              style={[
                styles.searchField,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <SearchIcon color={palette.textMuted} />
              <TextInput
                accessibilityLabel="Search employee"
                value={employeeQuery}
                onChangeText={setEmployeeQuery}
                placeholder="Search employee..."
                placeholderTextColor={palette.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                style={[styles.searchInput, { color: palette.textPrimary }]}
              />
            </View>

            {activeProgress ? (
              <ReportDownloadProgressView
                progress={activeProgress}
                canCancel={Boolean(activeProgress)}
                onCancel={() =>
                  activeProgress
                    ? downloads.cancelDownload(activeProgress.downloadId)
                    : undefined
                }
              />
            ) : null}

            {loading && rows.length === 0 ? (
              <AppLoader message="Loading WFH report" />
            ) : error && rows.length === 0 ? (
              <ErrorState message={error} onRetry={() => fetchReport(applied)} />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                title="No WFH records"
                message="No work-from-home rows matched the selected filters."
                actionTitle="Retry"
                onAction={() => fetchReport(applied)}
              />
            ) : (
              <View
                style={[
                  styles.tableCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                {truncated ? (
                  <Text style={themed.warning}>
                    Showing the first {PREVIEW_MAX_ROWS} rows. Export for the full
                    file.
                  </Text>
                ) : null}
                <ScrollView
                  horizontal
                  accessibilityLabel="WFH report table"
                >
                  <View>
                    <View
                      style={[
                        styles.headerRowTable,
                        { backgroundColor: palette.background },
                      ]}
                    >
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          { width: COL_EMPLOYEE },
                        ]}
                        accessibilityRole="header"
                      >
                        Employee
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          { width: COL_DATE },
                        ]}
                        accessibilityRole="header"
                      >
                        Date
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          { width: COL_TIME },
                        ]}
                        accessibilityRole="header"
                      >
                        Check In
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          { width: COL_TIME },
                        ]}
                        accessibilityRole="header"
                      >
                        Check Out
                      </Text>
                    </View>
                    {visibleRows.map((row, index) => (
                      <WfhTableRow
                        key={row.rowKey}
                        row={row}
                        palette={palette}
                        isLast={index === visibleRows.length - 1}
                      />
                    ))}
                  </View>
                </ScrollView>
              </View>
            )}

            <View style={styles.summaryRow}>
              {summaryMetrics.map(metric => (
                <View
                  key={metric.key}
                  accessibilityLabel={`${metric.label}: ${metric.value}`}
                  style={[
                    styles.summaryCard,
                    {
                      backgroundColor: metric.highlighted
                        ? `${palette.info}14`
                        : palette.surface,
                      borderColor: palette.border,
                      shadowColor: palette.black,
                    },
                  ]}
                >
                  <Text style={[styles.summaryValue, { color: metric.color }]}>
                    {metric.value}
                  </Text>
                  <Text
                    style={[styles.summaryLabel, { color: palette.textSecondary }]}
                    numberOfLines={2}
                  >
                    {metric.label}
                  </Text>
                </View>
              ))}
            </View>

            {capabilities.canExportCsv || capabilities.canExportXlsx ? (
              <View style={styles.exportRow}>
                {capabilities.canExportCsv ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export PDF"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'CSV'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('CSV').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.info,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <Text style={[styles.exportLabel, { color: palette.info }]}>
                      Export PDF
                    </Text>
                  </Pressable>
                ) : null}
                {capabilities.canExportXlsx ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export Excel"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'XLSX'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('XLSX').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: EXCEL_GREEN,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <ExcelGlyph color={EXCEL_GREEN} />
                    <Text style={[styles.exportLabel, { color: EXCEL_GREEN }]}>
                      Export Excel
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>
        </AppScreen>
      </PermissionGuard>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md },
  bellRoot: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  bellBody: {
    width: 14,
    height: 14,
    borderWidth: 2,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
  },
  bellClapper: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 1,
  },
  dateRangeField: {
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dateRangeLabel: {
    ...typography.body,
    fontWeight: '600',
    flex: 1,
  },
  calendarGlyph: {
    fontSize: 16,
    lineHeight: 18,
  },
  advancedCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.sm,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  filterRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dropdownButton: {
    flex: 1,
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dropdownLabel: {
    ...typography.caption,
    fontWeight: '600',
    flex: 1,
  },
  pickerPanel: {
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
  },
  pickerOption: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  searchField: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchIcon: {
    width: 16,
    height: 16,
    position: 'relative',
  },
  searchCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
  },
  searchHandle: {
    position: 'absolute',
    width: 7,
    height: 2,
    borderRadius: 1,
    right: 0,
    bottom: 1,
    transform: [{ rotate: '45deg' }],
  },
  searchInput: {
    ...typography.body,
    flex: 1,
    paddingVertical: spacing.sm,
  },
  tableCard: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    paddingBottom: spacing.xs,
  },
  headerRowTable: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
  },
  headerCellPad: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 56,
    paddingVertical: spacing.sm,
  },
  employeeCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  employeeName: {
    ...typography.caption,
    fontWeight: '600',
    flex: 1,
  },
  cellText: {
    ...typography.caption,
    paddingHorizontal: spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  summaryCard: {
    flex: 1,
    minHeight: 84,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 2,
  },
  summaryValue: {
    ...typography.screenTitle,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: '700',
  },
  summaryLabel: {
    ...typography.caption,
    fontWeight: '600',
    textAlign: 'center',
  },
  exportRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  exportButton: {
    flex: 1,
    minHeight: 48,
    borderWidth: 1.5,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  exportLabel: {
    ...typography.body,
    fontWeight: '700',
  },
  excelGlyph: {
    width: 18,
    height: 18,
    borderWidth: 1.5,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  excelGlyphText: {
    fontSize: 11,
    fontWeight: '800',
    lineHeight: 12,
  },
});
